import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import PopupComponent from "@components/PopupComponent";
import Loader from "@components/LoaderComponent/loader";
import FilterComponent from "@components/FilterComponent";
import { fetchDynamicFilters, humanize } from "@helpers/helpers";
import {
  saveToLocalStorage,
  getLocalConfig,
} from "@components/CustomTable/helpers";
import { Observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@stores/ThemeContext";

import clsx from "clsx";
import GradientText from "@/components/GradiantTitle";

import { ZERO_VALUE, ONE_VALUE } from "@utils/constants";
import { PADSTART_INDEX } from "./constants";

import { MoveLeft, MoveRight } from "lucide-react";

function refactorFilters(filters, translate) {
  const overrideKeys = {
    // ReconciliationStreamTag: "ReconciliationStream",
  };
  const translations = {
    AreaId: translate("charts.filters.AreaId"),
    OutletId: translate("charts.filters.OutletId"),
  };

  const mappedFilters =
    filters?.map((filter) => ({
      key: overrideKeys[filter.ColumnName] || filter.ColumnName,
      displayName:
        translations[filter.ColumnName] || humanize(filter.ColumnName),
      isMandatory: filter.IsMandatory,
      components: [
        {
          key: overrideKeys[filter.ColumnName] || filter.ColumnName,
          displayName:
            translations[filter.ColumnName] || humanize(filter.ColumnName),
          type: "checkboxGroup",
          values: [
            {
              displayName: "All",
              value: null,
              selectable: false,
              children: filter.Values.map((value) => ({
                displayName: value.Label,
                value: value.Value,
                selectable: true,
              })),
            },
          ],
          filterType: "checkbox",
          operator: "EQ",
        },
      ],
    })) || [];

  const businessDayFilter = {
    key: "businessDay",
    displayName: translate("charts.filters.businessDay"),
    components: [
      {
        name: translate("charts.filters.selectDate"),
        type: "title",
        key: "title",
      },
      {
        name: translate("charts.filters.businessDay"),
        type: "dateInput",
        inputType: "date",
        key: "BusinessDay",
        placeHolder: translate("charts.filters.businessDay"),
        prefix: "",
        filterType: "date",
      },
    ],
  };
  return [
    {
      mainFilters: [...mappedFilters, businessDayFilter],
    },
  ];
}

const getCurrentFormattedDate = () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + ONE_VALUE).padStart(
    PADSTART_INDEX,
    "0"
  );
  const day = String(date.getDate()).padStart(PADSTART_INDEX, "0");
  const hours = String(date.getHours()).padStart(PADSTART_INDEX, "0");
  const minutes = String(date.getMinutes()).padStart(PADSTART_INDEX, "0");
  const seconds = String(date.getSeconds()).padStart(PADSTART_INDEX, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const ChartComponent = ({
  title,
  ChartTag,
  ChartTypeTag,
  ScaleTag,
  maxSeriesPerPage,
  uniqueID,
  myStore,
  dynamicFilterTag,
  filterMinHeight,
}) => {
  const [openFilterPopup, setOpenFilterPopup] = useState(false);
  const [selectedValues, setSelectedValues] = useState({});
  const [parentSelectedValues, setParentSelectedValues] = useState({});
  const [appliedFilters, setAppliedFilters] = useState(
    getLocalConfig(`chartsFilters_${uniqueID}`, "") || {}
  );
  const [filterDisplayValues, setFilterDisplayValues] = useState({});
  const [filters, setFilters] = useState([]);
  const [lastUpdated, setLastUpdated] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { t: translate, i18n } = useTranslation();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    myStore.setChartParams(
      ChartTag,
      ChartTypeTag,
      ScaleTag,
      maxSeriesPerPage,
      uniqueID,
      isDarkMode
    );
    myStore.fillData((data) => {
      setLastUpdated(getCurrentFormattedDate());
    }, appliedFilters);
    fetchDynamicFilters(dynamicFilterTag, (newFilters) => {
      setFilters(newFilters);
    });
  }, [ChartTag, ChartTypeTag, ScaleTag, isDarkMode]);

  if (myStore.error) {
    throw myStore.error;
  }

  const saveAndApplyFilter = (filters) => {
    saveToLocalStorage(`chartsFilters_${uniqueID}`, filters);
    setAppliedFilters(filters);
    myStore.setAppliedFilters(filters, true, () => {
      setLastUpdated(getCurrentFormattedDate());
    });
  };

  const selectedFiltersDisplay = {
    PaymentMethodTag: translate("charts.filters.PaymentMethodTag"),
    ReconciliationStreamTag: translate(
      "charts.filters.ReconciliationStreamTag"
    ),
    AreaId: translate("charts.filters.AreaId"),
    OutletId: translate("charts.filters.OutletId"),
    TransactionTerminalCode: translate(
      "charts.filters.TransactionTerminalCode"
    ),
    BusinessDay: translate("charts.filters.businessDay"),
  };
  const commonPaginationArrowsStyles =
    "inline-flex rtl:rotate-180 items-center h-fit justify-center px-5 disabled:opacity-10 text-primary-400 hover:text-primary-600 disabled:cursor-not-allowed hover:scale-110 transition-transform transition-colors duration-300";

  return (
    <Observer>
      {() => {
        return (
          <div className="p-4 dark:bg-gray-700 bg-white rounded shadow-md">
            <div className="flex justify-between mb-4 items-baseline">
              <div className="flex gap-4 items-center">
                <GradientText className="!text-xl">{title} </GradientText>
              </div>
              <span className="text-xs text-primary-300">
                {lastUpdated &&
                  !myStore.loading &&
                  `${translate("charts.last-updated")}:  ${lastUpdated}`}
              </span>
            </div>
            <div
              className={clsx(
                "flex gap-4 justify-between relative",
                myStore.loading && "pointer-events-none opacity-50"
              )}
            >
              <FilterComponent
                appliedFilters={appliedFilters}
                updateAppliedFilters={saveAndApplyFilter}
                openFilter={openFilterPopup}
                setOpenFilter={setOpenFilterPopup}
                filterDisplayValues={filterDisplayValues}
                filters={refactorFilters(filters, translate)}
                selectedFiltersDisplay={selectedFiltersDisplay}
                setSelectedValues={(values) => {
                  setSelectedValues(values);
                  setParentSelectedValues(values);
                }}
                selectedValues={selectedValues}
                multipleValueColumns={[]}
                extraParentClass="!flex-row items-center"
                isScrollable={false}
                prefix={uniqueID}
                minHeight={filterMinHeight}
              />
            </div>
            {myStore.loading ? (
              <div className="relative min-h-[400px] flex items-center justify-center">
                <Loader dots />
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center">
                  {myStore.data?.shouldPaginate && (
                    <button
                      onClick={() => myStore.handlePreviousPage()}
                      disabled={myStore.data.hasNextPage === false}
                      className={clsx(
                        commonPaginationArrowsStyles,
                        "pl-2 pr-4"
                      )}
                    >
                      <MoveLeft />
                    </button>
                  )}
                  <input
                    type="hidden"
                    id={`current_page_${uniqueID}`}
                    value={myStore.currentPage}
                  />
                  {myStore.loading ? (
                    <div className="relative min-h-[400px] w-full flex items-center justify-center">
                      <Loader dots />
                    </div>
                  ) : myStore.data.chartOptions ? (
                    <Chart
                      options={myStore.data.chartOptions}
                      series={myStore.data.chartSeries}
                      type="line"
                      style={{ width: "100%" }}
                      height={400}
                    />
                  ) : (
                    (!myStore.data.chartSeries ||
                      !myStore.data.chartOptions) && (
                      <div className="flex w-full items-center justify-center min-h-[400px]">
                        <span className="text-xl text-primary-300">
                          {errorMessage || "No data available"}
                        </span>
                      </div>
                    )
                  )}
                  {myStore.data?.shouldPaginate && (
                    <button
                      onClick={() => myStore.handleNextPage()}
                      disabled={myStore.offset === ZERO_VALUE}
                      className={clsx(
                        commonPaginationArrowsStyles,
                        "pr-2 pl-4"
                      )}
                    >
                      <MoveRight />
                    </button>
                  )}
                </div>
              </div>
            )}
            {myStore.popupData && (
              <PopupComponent
                open={myStore.popupData !== null}
                innerHtml={myStore.popupData}
                setPopupOpen={() => {
                  myStore.popupData = null;
                }}
              />
            )}
          </div>
        );
      }}
    </Observer>
  );
};

export default ChartComponent;
